import { useChain } from '@/hooks/Web3ModalProvider';
import type { VaultInfoEntity } from '@/lib/entities/vault.entity';
import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosRequestConfig } from 'axios';
import axios from 'axios';
import { compile } from 'path-to-regexp';
import { useCallback, useEffect } from 'react';

export type ArrayElement<T> = T extends Array<infer type> ? type : never;

export const RESOURCES = {
  vault_list: {
    path: '/vaults/all/',
    method: 'GET',
  },
  vault_details: {
    path: '/vaults/:vaultAddress',
    method: 'GET',
    pathParams: ['vaultAddress' as const],
  },
  vault_tvl: {
    path: '/vaults/tvl',
    method: 'GET',
  },
};

export type ResourceName = keyof typeof RESOURCES;

type ResourcePathParamName<Resource extends ResourceName> = (typeof RESOURCES)[Resource] extends {
  pathParams: Array<string>;
}
  ? ArrayElement<(typeof RESOURCES)[Resource]['pathParams']>
  : string;

export type ResourcePathParams<Resource extends ResourceName> =
  (typeof RESOURCES)[Resource] extends { pathParams: Array<string> }
    ? Record<ResourcePathParamName<Resource>, string | undefined>
    : never;

export type ResourceResponse<Q extends ResourceName> = Q extends 'vault_list'
  ? VaultInfoEntity[]
  : Q extends 'vault_details'
    ? VaultInfoEntity
    : Q extends 'vault_tvl'
      ? number
      : never;

const buildUrl = <R extends ResourceName>(
  chainId: number,
  resource: R,
  pathParams: ResourcePathParams<R>,
  queryParams?: Record<string, string>
) => {
  const basePath = `/daemon/v1/api/${chainId}${compile(RESOURCES[resource].path)(pathParams)}`;
  const params = new URLSearchParams();
  if (queryParams) {
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.append(key, String(value));
      }
    });
  }

  const qs = params.toString();
  return qs ? `${basePath}?${qs}` : basePath;
};

export const useQueryFetch = () => {
  const { desiredChain } = useChain();
  const queryClient = useQueryClient();

  // Prefetch function for route-based data
  const prefetchData = useCallback(
    async (
      resource: ResourceName,
      options?: {
        pathParams?: ResourcePathParams<typeof resource>;
        queryParams?: Record<string, string>;
      }
    ) => {
      const url = buildUrl(
        desiredChain?.chainId,
        resource,
        options?.pathParams,
        options?.queryParams
      );

      await queryClient.prefetchQuery({
        queryKey: [RESOURCES[resource].path, options?.pathParams],
        queryFn: () =>
          axios(url, {
            method: RESOURCES[resource].method,
          }).then(res => res.data as ResourceResponse<typeof resource>),
        staleTime: 1000 * 60, // Data stays fresh for 1 minute
      });
    },
    [desiredChain, queryClient]
  );

  // Setup prefetching for linked resources
  useEffect(() => {
    // Prefetch related data when a vault is loaded
    if (RESOURCES['vault_list']) {
      prefetchData('vault_list');
    }
  }, [prefetchData]);

  return useCallback(
    <R extends ResourceName>(
      resource: R,
      options: {
        pathParams?: ResourcePathParams<R>;
        queryParams?: Record<string, string>;
        placeholderData?: ResourceResponse<R>;
        queryOptions?: Omit<UseQueryOptions<ResourceResponse<R>>, 'queryKey' | 'queryFn'>;
      } & Pick<AxiosRequestConfig, 'data'>
    ) => {
      const url = buildUrl(
        desiredChain?.chainId,
        resource,
        options.pathParams,
        options.queryParams
      );

      return useQuery({
        queryKey: [RESOURCES[resource].path, options.pathParams],
        placeholderData: options?.queryOptions?.placeholderData,
        staleTime: 1000 * 60, // Data stays fresh for 1 minute
        gcTime: 1000 * 60 * 5, // Cache data for 5 minutes
        queryFn: () =>
          axios(url, {
            method: RESOURCES[resource].method,
            data: options.data,
          }).then(res => res.data as ResourceResponse<R>),
        ...options.queryOptions,
      });
    },
    [desiredChain]
  );
};
